import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { isApiProblem } from '@shared/api/problem';
import { CLIENT_CODES } from '@shared/api/problem';
import { Alert } from './Feedback';

export interface DescribedError {
  title: string;
  detail?: string | undefined;
  requestId?: string | null | undefined;
}

/**
 * Human text for any thrown error. Problem titles/details are already localized by the API
 * (Accept-Language); network failures use the client catalog.
 */
export function describeError(error: unknown, t: TFunction<'common'>): DescribedError {
  if (isApiProblem(error)) {
    if (error.code === CLIENT_CODES.network) return { title: t('errors.network.title'), detail: t('errors.network.detail') };
    // No problem document (e.g. a route not deployed yet answers {"detail":"Not Found"}): the status text is
    // English-only, so use the localized catalog instead.
    if (error.code === 'HTTP_404') return { title: t('errors.unavailable') };
    if (/^HTTP_\d+$/.test(error.code)) return { title: t('errors.generic') };
    return { title: error.title || t('errors.generic'), detail: error.detail, requestId: error.instance };
  }
  return { title: t('errors.generic') };
}

/** Field errors from a problem, keyed by API field path ("organization.slug"). */
export function problemFieldErrors(error: unknown): Record<string, string> {
  return isApiProblem(error) ? error.fieldErrors() : {};
}

export interface FormErrorProps {
  error: unknown;
  className?: string;
}

/** Form-level error summary (role="alert"); field messages stay under their fields. */
export function FormError({ error, className }: FormErrorProps) {
  const { t } = useTranslation('common');
  if (!error) return null;
  const { title, detail, requestId } = describeError(error, t);
  return (
    <Alert tone="danger" title={title} live className={className}>
      {detail ? <p>{detail}</p> : null}
      {requestId ? (
        <p className="mt-2 flex flex-wrap items-baseline gap-x-1 text-xs opacity-80">
          <span>{t('errors.requestId')}</span>
          <bdi className="ltr-island whitespace-nowrap font-mono">{requestId}</bdi>
        </p>
      ) : null}
    </Alert>
  );
}
